import React, { useState, useEffect } from "react";
import { Award, Shield, CheckCircle, Star } from "lucide-react";
import { contentAPI } from "../services/api";
import { useLanguage } from "../hooks/useLanguage";

const Certificates = () => {
  const currentLanguage = useLanguage();
  const [sectionData, setSectionData] = useState(null);

  useEffect(() => {
    const fetchSectionData = async () => {
      try {
        const sections = await contentAPI.getSections("home").catch(() => []);
        const certificatesSection = sections.find(s => s.section_type === "certificates");
        if (certificatesSection?.content_data) {
          setSectionData(certificatesSection.content_data);
        }
      } catch (error) {
        console.error("Error fetching certificates section:", error);
      }
    };
    fetchSectionData();
  }, []);

  const getStringValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      return value[currentLanguage] || value['de'] || value[Object.keys(value)[0]] || fallback;
    }
    return String(value) || fallback;
  };

  const sectionTitle = sectionData ? getStringValue(sectionData.title, "Zertifikate & Qualifikationen") : "Zertifikate & Qualifikationen";
  const sectionDescription = sectionData ? getStringValue(sectionData.description, "Vertrauen Sie auf unsere professionelle Qualifikation und langjährige Erfahrung") : "Vertrauen Sie auf unsere professionelle Qualifikation und langjährige Erfahrung";
  
  const defaultCertificates = [
    {
      icon: Award,
      title: "MDK Note",
      description: "Qualitätsbewertung durch den Medizinischen Dienst der Krankenversicherung",
      placeholder: true
    },
    {
      icon: Shield,
      title: "Zertifizierungen",
      description: "Professionelle Zertifizierungen und Qualitätsstandards",
      placeholder: true
    },
    {
      icon: CheckCircle,
      title: "Qualifikationen",
      description: "Ausgebildete und qualifizierte Pflegefachkräfte",
      placeholder: true
    },
    {
      icon: Star,
      title: "Erfahrung",
      description: "Langjährige Erfahrung in der häuslichen Pflege",
      placeholder: true
    }
  ];

  // Use certificates from API if available
  const apiCertificates = sectionData?.certificates || [];
  const certificates = apiCertificates.length > 0
    ? apiCertificates.map((cert, index) => ({
        icon: defaultCertificates[index]?.icon || Award,
        title: getStringValue(cert.title, defaultCertificates[index]?.title || ""),
        description: getStringValue(cert.description, defaultCertificates[index]?.description || ""),
        image_url: cert.image_url || null
      }))
    : defaultCertificates;

  const trustIndicators = sectionData?.trust_indicators || [];

  return (
    <section id="zertifikate" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-[var(--vortex-surface)]">
      <div className="max-w-7xl mx-auto w-full min-w-0">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-[var(--vortex-text-primary)]">
            {sectionTitle}
          </h2>
          <p className="text-base sm:text-xl text-[var(--vortex-text-secondary)] max-w-3xl mx-auto leading-relaxed">
            {sectionDescription}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {certificates.map((cert, index) => {
            const IconComponent = cert.icon;
            return (
              <div
                key={index}
                className="bg-[var(--vortex-surface-alt)] p-4 sm:p-6 md:p-8 rounded-xl border-2 border-[var(--vortex-border)] hover:border-[var(--vortex-accent-primary)] transition-all hover:shadow-lg text-center"
              >
                <div className="w-20 h-20 rounded-full bg-[var(--vortex-accent-primary)]/10 flex items-center justify-center mx-auto mb-6">
                  <IconComponent className="w-10 h-10 text-[var(--vortex-accent-primary)]" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-[var(--vortex-text-primary)]">
                  {cert.title}
                </h3>
                <p className="text-base text-[var(--vortex-text-secondary)] leading-relaxed mb-6">
                  {cert.description}
                </p>
                {/* Certificate image */}
                {cert.image_url ? (
                  <div className="w-full h-48 rounded-lg overflow-hidden border-2 border-[var(--vortex-border)]">
                    <img src={cert.image_url} alt={cert.title} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-full h-48 bg-[var(--vortex-surface)] rounded-lg border-2 border-dashed border-[var(--vortex-border)] flex items-center justify-center">
                    <span className="text-[var(--vortex-text-muted)] text-sm">
                      Zertifikat Platzhalter
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Additional Trust Indicators */}
        {trustIndicators.length > 0 && (
          <div className="mt-10 sm:mt-16 bg-[var(--vortex-surface-alt)] p-4 sm:p-6 md:p-10 rounded-xl border-2 border-[var(--vortex-border)]">
            <div className={`grid gap-6 sm:gap-8 text-center ${trustIndicators.length === 1 ? 'md:grid-cols-1' : trustIndicators.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
              {trustIndicators.map((indicator, index) => (
                <div key={index}>
                  <div className="text-4xl font-bold text-[var(--vortex-accent-primary)] mb-2">
                    ✓
                  </div>
                  <p className="text-lg text-[var(--vortex-text-primary)] font-semibold">
                    {getStringValue(indicator.title)}
                  </p>
                  <p className="text-base text-[var(--vortex-text-secondary)] mt-2">
                    {getStringValue(indicator.description)}
                  </p>
                  {indicator.subtitle && (
                    <p className="text-sm text-[var(--vortex-text-muted)] mt-1">
                      {getStringValue(indicator.subtitle)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Certificates;

